import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Textarea + push-to-talk microphone using the browser Web Speech API.
 * Falls back gracefully (mic button hidden) when the API isn't available
 * — therapist can still type.
 *
 * Web Speech API is free, browser-native, no server roundtrip.
 * Works on Chrome, Edge, Safari (with permission prompt) — not Firefox.
 */
export default function VoiceTextarea({
  value,
  onChange,
  onBlur,
  rows = 4,
  placeholder = "",
  testId = "voice-textarea",
  className = "",
}) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const interimRef = useRef("");
  const supported = typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    if (!supported) return;
    const Rec = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new Rec();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = navigator.language || "en-US";

    rec.onresult = (event) => {
      let final = "";
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += transcript;
        else interim += transcript;
      }
      if (final) {
        const sep = value && !value.endsWith(" ") ? " " : "";
        onChange(`${value}${sep}${final.trim()}`);
        interimRef.current = "";
      } else {
        interimRef.current = interim;
      }
    };
    rec.onerror = (e) => {
      if (e.error !== "aborted" && e.error !== "no-speech") {
        toast.error(`Mic error: ${e.error}`);
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    return () => { try { rec.abort(); } catch { /* noop */ } };
    // We intentionally read latest `value` in onresult via closure — the effect
    // rebinds whenever value changes so the most recent text is appended cleanly.
  }, [value, onChange, supported]);

  const toggle = () => {
    if (!supported) {
      toast.error("Your browser doesn't support voice input. Try Chrome or Safari.");
      return;
    }
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      rec.stop();
      setListening(false);
    } else {
      try {
        rec.start();
        setListening(true);
      } catch {
        // Some browsers throw if start() is called twice quickly
        setListening(false);
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <textarea
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        rows={rows}
        placeholder={placeholder}
        className="w-full px-4 py-3 pr-14 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none resize-y"
      />
      {supported && (
        <button
          type="button"
          onClick={toggle}
          data-testid={`${testId}-mic-btn`}
          aria-label={listening ? "Stop recording" : "Start voice input"}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            listening
              ? "bg-terracotta-500 text-white shadow-md animate-pulse"
              : "bg-white border border-paper-200 text-ink-700 hover:bg-paper-100 hover:border-sage-500/40"
          }`}
          title={listening ? "Stop recording" : "Start voice input"}
        >
          {listening ? <MicOff className="w-4 h-4" strokeWidth={1.75} /> : <Mic className="w-4 h-4" strokeWidth={1.75} />}
        </button>
      )}
      {listening && (
        <div className="absolute bottom-3 right-14 text-xs text-terracotta-600 font-medium" data-testid={`${testId}-listening`}>
          ● Listening…
        </div>
      )}
    </div>
  );
}
